/**
 * FLO SIGNATURE DESIGN SYSTEM v2 - UI Primitives
 * SINGLE SOURCE OF TRUTH - All UI components should use these primitives
 *
 * Components:
 * - Layout: Screen, Panel, Section, Divider, Card, CardContent
 * - Forms: Button, Input, TextArea, Select, Checkbox, FormField
 * - Feedback: Alert, Spinner, LoadingOverlay, Badge
 * - Data: Table, TableHead, TableHeader, TableBody, TableRow, TableCell
 * - Overlay: Modal, Tabs
 */

import React from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { SPACING, RADIUS, COLORS, TYPOGRAPHY, Z_INDEX } from './tokens';

// ============================================================================
// LAYOUT PRIMITIVES
// ============================================================================

// Screen (background, padding, max width)
export const Screen: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = ''
}) => (
  <div
    className={className}
    style={{
      backgroundColor: COLORS.bg.base,
      minHeight: '100vh',
      padding: SPACING[16],
    }}
  >
    {children}
  </div>
);

// Panel/Card (structured container)
export const Panel: React.FC<{
  children: React.ReactNode;
  className?: string;
  noPadding?: boolean;
  style?: React.CSSProperties;
}> = ({ children, className = '', noPadding = false, style = {} }) => (
  <div
    className={className}
    style={{
      backgroundColor: COLORS.bg.panel,
      border: `1px solid ${COLORS.border.default}`,
      borderRadius: RADIUS,
      padding: noPadding ? 0 : SPACING[12],
      ...style,
    }}
  >
    {children}
  </div>
);

// Card (alias for Panel without padding, for ui/ compatibility)
export const Card: React.FC<React.HTMLAttributes<HTMLDivElement> & { children: React.ReactNode }> = ({
  children,
  className = '',
  style,
  ...props
}) => (
  <div
    style={{
      backgroundColor: COLORS.bg.panel,
      border: `1px solid ${COLORS.border.default}`,
      borderRadius: RADIUS,
      ...style,
    }}
    className={className}
    {...props}
  >
    {children}
  </div>
);

export const CardContent: React.FC<React.HTMLAttributes<HTMLDivElement> & { children: React.ReactNode }> = ({
  children,
  className = '',
  style,
  ...props
}) => (
  <div
    style={{
      padding: SPACING[16],
      ...style,
    }}
    className={className}
    {...props}
  >
    {children}
  </div>
);

// Section (title + content)
export const Section: React.FC<{
  title?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}> = ({ title, children, action, className = '' }) => (
  <div className={className} style={{ marginBottom: SPACING[16] }}>
    {title && (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: SPACING[12]
        }}
      >
        <h2
          style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h2,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            margin: 0,
          }}
        >
          {title}
        </h2>
        {action}
      </div>
    )}
    {children}
  </div>
);

// Divider (subtle structural separation)
export const Divider: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div
    className={className}
    style={{
      height: '1px',
      backgroundColor: COLORS.border.default,
      margin: `${SPACING[12]} 0`,
    }}
  />
);

// ============================================================================
// BUTTON
// ============================================================================

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  loadingText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  icon?: React.ReactNode; // Legacy support
  fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  children,
  onClick,
  type = 'button',
  disabled = false,
  isLoading = false,
  loadingText,
  leftIcon,
  rightIcon,
  icon, // Legacy support
  fullWidth = false,
  className = '',
  style: customStyle,
  ...props
}) => {
  const variantStyles = {
    primary: {
      backgroundColor: COLORS.accent.default,
      color: '#FFFFFF',
      border: 'none',
      hover: COLORS.accent.hover,
    },
    secondary: {
      backgroundColor: 'transparent',
      color: COLORS.text.primary,
      border: `1px solid ${COLORS.border.default}`,
      hover: COLORS.bg.hover,
    },
    ghost: {
      backgroundColor: 'transparent',
      color: COLORS.text.secondary,
      border: 'none',
      hover: COLORS.bg.hover,
    },
    danger: {
      backgroundColor: 'transparent',
      color: COLORS.status.error,
      border: `1px solid ${COLORS.status.error}`,
      hover: 'rgba(239, 68, 68, 0.1)',
    },
    success: {
      backgroundColor: COLORS.status.success,
      color: '#FFFFFF',
      border: 'none',
      hover: '#059669',
    },
    outline: {
      backgroundColor: 'transparent',
      color: COLORS.accent.default,
      border: `2px solid ${COLORS.accent.default}`,
      hover: 'rgba(59, 130, 246, 0.1)',
    },
  };

  const sizeStyles = {
    sm: {
      height: '32px',
      padding: `0 ${SPACING[12]}`,
      fontSize: TYPOGRAPHY.body.sizes.small,
      gap: SPACING[4],
    },
    md: {
      height: '40px',
      padding: `0 ${SPACING[16]}`,
      fontSize: TYPOGRAPHY.body.sizes.default,
      gap: SPACING[8],
    },
    lg: {
      height: '48px',
      padding: `0 ${SPACING[24]}`,
      fontSize: TYPOGRAPHY.body.sizes.default,
      gap: SPACING[8],
    },
  };

  const style = variantStyles[variant];
  const sizeStyle = sizeStyles[size];
  const effectiveLeftIcon = leftIcon || icon;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: sizeStyle.height,
        padding: sizeStyle.padding,
        gap: sizeStyle.gap,
        fontFamily: TYPOGRAPHY.body.fontFamily,
        fontSize: sizeStyle.fontSize,
        fontWeight: '500',
        borderRadius: RADIUS,
        cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
        opacity: disabled || isLoading ? 0.5 : 1,
        transition: 'all 0.1s ease',
        backgroundColor: style.backgroundColor,
        color: style.color,
        border: style.border,
        width: fullWidth ? '100%' : 'auto',
        outline: 'none',
        ...customStyle,
      }}
      onMouseEnter={(e) => !disabled && !isLoading && (e.currentTarget.style.backgroundColor = style.hover)}
      onMouseLeave={(e) => !disabled && !isLoading && (e.currentTarget.style.backgroundColor = style.backgroundColor)}
      {...props}
    >
      {isLoading && (
        <ArrowPathIcon style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} aria-hidden="true" />
      )}
      {!isLoading && effectiveLeftIcon && effectiveLeftIcon}
      {isLoading ? (loadingText || children) : children}
      {!isLoading && rightIcon && rightIcon}
    </button>
  );
};

// ============================================================================
// FORM INPUTS
// ============================================================================

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  helperText?: string;
  icon?: React.ReactNode; // Legacy support for left icon
}

export const Input: React.FC<InputProps> = ({
  className = '',
  error,
  helperText,
  icon,
  style,
  ...props
}) => {
  const baseStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    borderRadius: RADIUS,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: error ? COLORS.status.error : COLORS.border.default,
    padding: `${SPACING[8]} ${SPACING[12]}`,
    paddingLeft: icon ? SPACING[32] : SPACING[12],
    fontFamily: TYPOGRAPHY.body.fontFamily,
    fontSize: TYPOGRAPHY.body.sizes.default,
    color: COLORS.text.primary,
    backgroundColor: COLORS.bg.panel,
    outline: 'none',
    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
  };

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      {icon && (
        <div
          style={{
            position: 'absolute',
            left: SPACING[8],
            top: '50%',
            transform: 'translateY(-50%)',
            color: COLORS.text.tertiary,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {icon}
        </div>
      )}
      <input
        style={{ ...baseStyle, ...style }}
        className={className}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? 'error-message' : helperText ? 'helper-text' : undefined}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = error ? COLORS.status.error : COLORS.accent.default;
          e.currentTarget.style.boxShadow = `0 0 0 3px ${error ? COLORS.status.error : COLORS.accent.default}20`;
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = error ? COLORS.status.error : COLORS.border.default;
          e.currentTarget.style.boxShadow = 'none';
          props.onBlur?.(e);
        }}
        {...props}
      />
      {error && (
        <p
          style={{
            marginTop: SPACING[4],
            fontSize: TYPOGRAPHY.body.sizes.small,
            color: COLORS.status.error,
          }}
          id="error-message"
        >
          {error}
        </p>
      )}
      {helperText && !error && (
        <p
          style={{
            marginTop: SPACING[4],
            fontSize: TYPOGRAPHY.body.sizes.small,
            color: COLORS.text.secondary,
          }}
          id="helper-text"
        >
          {helperText}
        </p>
      )}
    </div>
  );
};

interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: string;
  helperText?: string;
}

export const TextArea: React.FC<TextAreaProps> = ({
  className = '',
  error,
  helperText,
  rows = 3,
  style,
  ...props
}) => {
  const baseStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    borderRadius: RADIUS,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: error ? COLORS.status.error : COLORS.border.default,
    padding: `${SPACING[8]} ${SPACING[12]}`,
    fontFamily: TYPOGRAPHY.body.fontFamily,
    fontSize: TYPOGRAPHY.body.sizes.default,
    color: COLORS.text.primary,
    backgroundColor: COLORS.bg.panel,
    outline: 'none',
    resize: 'vertical',
    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
  };

  return (
    <div style={{ width: '100%' }}>
      <textarea
        rows={rows}
        style={{ ...baseStyle, ...style }}
        className={className}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? 'error-message' : helperText ? 'helper-text' : undefined}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = error ? COLORS.status.error : COLORS.accent.default;
          e.currentTarget.style.boxShadow = `0 0 0 3px ${error ? COLORS.status.error : COLORS.accent.default}20`;
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = error ? COLORS.status.error : COLORS.border.default;
          e.currentTarget.style.boxShadow = 'none';
          props.onBlur?.(e);
        }}
        {...props}
      />
      {error && (
        <p
          style={{
            marginTop: SPACING[4],
            fontSize: TYPOGRAPHY.body.sizes.small,
            color: COLORS.status.error,
          }}
          id="error-message"
        >
          {error}
        </p>
      )}
      {helperText && !error && (
        <p
          style={{
            marginTop: SPACING[4],
            fontSize: TYPOGRAPHY.body.sizes.small,
            color: COLORS.text.secondary,
          }}
          id="helper-text"
        >
          {helperText}
        </p>
      )}
    </div>
  );
};

// Alias for backwards compatibility
export const Textarea = TextArea;

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  error?: string;
  helperText?: string;
  options?: { value: string; label: string }[];
  children?: React.ReactNode;
}

export const Select: React.FC<SelectProps> = ({
  className = '',
  error,
  helperText,
  options,
  children,
  style,
  ...props
}) => {
  const baseStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    borderRadius: RADIUS,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: error ? COLORS.status.error : COLORS.border.default,
    padding: `${SPACING[8]} ${SPACING[12]}`,
    fontFamily: TYPOGRAPHY.body.fontFamily,
    fontSize: TYPOGRAPHY.body.sizes.default,
    color: COLORS.text.primary,
    backgroundColor: COLORS.bg.panel,
    outline: 'none',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
  };

  return (
    <div style={{ width: '100%' }}>
      <select
        style={{ ...baseStyle, ...style }}
        className={className}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? 'error-message' : helperText ? 'helper-text' : undefined}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = error ? COLORS.status.error : COLORS.accent.default;
          e.currentTarget.style.boxShadow = `0 0 0 3px ${error ? COLORS.status.error : COLORS.accent.default}20`;
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = error ? COLORS.status.error : COLORS.border.default;
          e.currentTarget.style.boxShadow = 'none';
          props.onBlur?.(e);
        }}
        {...props}
      >
        {options ? options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        )) : children}
      </select>
      {error && (
        <p
          style={{
            marginTop: SPACING[4],
            fontSize: TYPOGRAPHY.body.sizes.small,
            color: COLORS.status.error,
          }}
          id="error-message"
        >
          {error}
        </p>
      )}
      {helperText && !error && (
        <p
          style={{
            marginTop: SPACING[4],
            fontSize: TYPOGRAPHY.body.sizes.small,
            color: COLORS.text.secondary,
          }}
          id="helper-text"
        >
          {helperText}
        </p>
      )}
    </div>
  );
};

interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  className = '',
  label,
  id,
  style,
  ...props
}) => {
  const baseStyle: React.CSSProperties = {
    height: '16px',
    width: '16px',
    borderRadius: RADIUS,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: COLORS.border.default,
    accentColor: COLORS.accent.default,
    cursor: 'pointer',
  };

  if (label) {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <input
          type="checkbox"
          id={id}
          style={{ ...baseStyle, ...style }}
          className={className}
          {...props}
        />
        <label
          htmlFor={id}
          style={{
            marginLeft: SPACING[8],
            display: 'block',
            fontSize: TYPOGRAPHY.body.sizes.small,
            color: COLORS.text.primary,
            cursor: 'pointer',
          }}
        >
          {label}
        </label>
      </div>
    );
  }

  return (
    <input
      type="checkbox"
      id={id}
      style={{ ...baseStyle, ...style }}
      className={className}
      {...props}
    />
  );
};

interface FormFieldProps {
  label?: string;
  htmlFor?: string;
  required?: boolean;
  error?: string;
  helperText?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  htmlFor,
  required = false,
  error,
  helperText,
  children,
  style,
}) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: SPACING[4],
      ...style,
    }}
  >
    {label && (
      <label
        htmlFor={htmlFor}
        style={{
          display: 'block',
          fontSize: TYPOGRAPHY.body.sizes.small,
          fontWeight: TYPOGRAPHY.weight.medium,
          color: COLORS.text.primary,
          fontFamily: TYPOGRAPHY.body.fontFamily,
        }}
      >
        {label}
        {required && (
          <span style={{ color: COLORS.status.error, marginLeft: SPACING[4] }}>*</span>
        )}
      </label>
    )}
    {children}
    {error && (
      <p
        style={{
          margin: 0,
          marginTop: SPACING[4],
          fontSize: TYPOGRAPHY.body.sizes.small,
          color: COLORS.status.error,
          fontFamily: TYPOGRAPHY.body.fontFamily,
        }}
        role="alert"
      >
        {error}
      </p>
    )}
    {helperText && !error && (
      <p
        style={{
          margin: 0,
          marginTop: SPACING[4],
          fontSize: TYPOGRAPHY.body.sizes.small,
          color: COLORS.text.secondary,
          fontFamily: TYPOGRAPHY.body.fontFamily,
        }}
      >
        {helperText}
      </p>
    )}
  </div>
);

// ============================================================================
// FEEDBACK COMPONENTS
// ============================================================================

type AlertVariant = 'success' | 'error' | 'warning' | 'info';

interface AlertProps {
  variant?: AlertVariant;
  children: React.ReactNode;
  onClose?: () => void;
  style?: React.CSSProperties;
}

export const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  children,
  onClose,
  style,
}) => {
  const variants = {
    success: {
      container: {
        backgroundColor: 'rgba(16, 185, 129, 0.15)',
        borderColor: COLORS.status.success,
        color: COLORS.status.success,
      },
      icon: CheckCircleIcon,
      iconColor: COLORS.status.success,
    },
    error: {
      container: {
        backgroundColor: 'rgba(239, 68, 68, 0.15)',
        borderColor: COLORS.status.error,
        color: COLORS.status.error,
      },
      icon: ExclamationCircleIcon,
      iconColor: COLORS.status.error,
    },
    warning: {
      container: {
        backgroundColor: 'rgba(245, 158, 11, 0.15)',
        borderColor: COLORS.status.warning,
        color: COLORS.status.warning,
      },
      icon: ExclamationTriangleIcon,
      iconColor: COLORS.status.warning,
    },
    info: {
      container: {
        backgroundColor: 'rgba(59, 130, 246, 0.15)',
        borderColor: COLORS.accent.default,
        color: COLORS.accent.default,
      },
      icon: InformationCircleIcon,
      iconColor: COLORS.accent.default,
    },
  };

  const config = variants[variant];
  const Icon = config.icon;

  return (
    <div
      style={{
        padding: SPACING[12],
        borderRadius: RADIUS,
        border: `1px solid ${config.container.borderColor}`,
        display: 'flex',
        alignItems: 'flex-start',
        backgroundColor: config.container.backgroundColor,
        color: config.container.color,
        ...style,
      }}
      role="alert"
    >
      <Icon style={{ width: '20px', height: '20px', marginRight: SPACING[8], flexShrink: 0, color: config.iconColor }} />
      <div style={{ flex: 1, fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.small }}>{children}</div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          style={{
            marginLeft: SPACING[8],
            flexShrink: 0,
            color: COLORS.text.tertiary,
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            transition: 'color 0.1s ease',
          }}
          aria-label="Close"
          onMouseEnter={(e) => (e.currentTarget.style.color = COLORS.text.secondary)}
          onMouseLeave={(e) => (e.currentTarget.style.color = COLORS.text.tertiary)}
        >
          <XMarkIcon style={{ width: '20px', height: '20px' }} />
        </button>
      )}
    </div>
  );
};

type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl';

interface SpinnerProps {
  size?: SpinnerSize;
  label?: string;
  style?: React.CSSProperties;
}

export const Spinner: React.FC<SpinnerProps> = ({ size = 'md', label, style }) => {
  const sizeStyles = {
    sm: '16px',
    md: '24px',
    lg: '32px',
    xl: '48px',
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
    >
      <ArrowPathIcon
        style={{
          width: sizeStyles[size],
          height: sizeStyles[size],
          animation: 'spin 1s linear infinite',
          color: COLORS.accent.default,
        }}
        aria-hidden="true"
      />
      {label && (
        <span
          style={{
            marginTop: SPACING[8],
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.small,
            color: COLORS.text.secondary,
          }}
          role="status"
        >
          {label}
        </span>
      )}
      <span
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          borderWidth: 0,
        }}
      >
        Loading...
      </span>
    </div>
  );
};

interface LoadingOverlayProps {
  message?: string;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ message = 'Loading...' }) => (
  <div
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: Z_INDEX.modal,
    }}
  >
    <div style={{ textAlign: 'center' }}>
      <Spinner size="xl" />
      <p
        style={{
          marginTop: SPACING[16],
          fontFamily: TYPOGRAPHY.body.fontFamily,
          fontSize: TYPOGRAPHY.body.sizes.default,
          color: COLORS.text.primary,
        }}
      >
        {message}
      </p>
    </div>
  </div>
);

// Badge/Pill (status, filters)
type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'error' | 'info' | 'accent';
type BadgeSize = 'sm' | 'md' | 'lg';

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  color?: BadgeVariant; // Legacy alias
  size?: BadgeSize;
  className?: string;
  style?: React.CSSProperties;
}

export const Badge: React.FC<BadgeProps> = ({
  children,
  variant,
  color,
  size = 'md',
  className = '',
  style: customStyle,
}) => {
  // Support both 'variant' and legacy 'color' prop
  const effectiveVariant = variant || color || 'default';

  const variantStyles = {
    default: { bg: COLORS.bg.hover, text: COLORS.text.secondary },
    primary: { bg: 'rgba(59, 130, 246, 0.15)', text: COLORS.accent.default },
    accent: { bg: 'rgba(59, 130, 246, 0.15)', text: COLORS.accent.default },
    success: { bg: 'rgba(16, 185, 129, 0.15)', text: COLORS.status.success },
    warning: { bg: 'rgba(245, 158, 11, 0.15)', text: COLORS.status.warning },
    danger: { bg: 'rgba(239, 68, 68, 0.15)', text: COLORS.status.error },
    error: { bg: 'rgba(239, 68, 68, 0.15)', text: COLORS.status.error },
    info: { bg: 'rgba(59, 130, 246, 0.15)', text: COLORS.accent.default },
  };

  const sizeStyles = {
    sm: {
      padding: `${SPACING[4]} ${SPACING[8]}`,
      fontSize: TYPOGRAPHY.body.sizes.small,
    },
    md: {
      padding: `${SPACING[4]} ${SPACING[12]}`,
      fontSize: TYPOGRAPHY.body.sizes.small,
    },
    lg: {
      padding: `${SPACING[8]} ${SPACING[12]}`,
      fontSize: TYPOGRAPHY.body.sizes.default,
    },
  };

  const style = variantStyles[effectiveVariant];
  const sizeStyle = sizeStyles[size];

  return (
    <span
      className={className}
      style={{
        display: 'inline-block',
        padding: sizeStyle.padding,
        fontFamily: TYPOGRAPHY.body.fontFamily,
        fontSize: sizeStyle.fontSize,
        fontWeight: '500',
        borderRadius: RADIUS,
        backgroundColor: style.bg,
        color: style.text,
        ...customStyle,
      }}
    >
      {children}
    </span>
  );
};

// ============================================================================
// DATA TABLE
// ============================================================================

export const Table: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = ''
}) => (
  <div
    className={className}
    style={{
      width: '100%',
      overflowX: 'auto',
    }}
  >
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      {children}
    </table>
  </div>
);

export const TableHead: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <thead
    style={{
      borderBottom: `1px solid ${COLORS.border.default}`,
    }}
  >
    {children}
  </thead>
);

export const TableHeader: React.FC<{ children: React.ReactNode; align?: 'left' | 'center' | 'right' }> = ({
  children,
  align = 'left'
}) => (
  <th
    style={{
      padding: `${SPACING[4]} ${SPACING[8]}`,
      fontFamily: TYPOGRAPHY.body.fontFamily,
      fontSize: TYPOGRAPHY.body.sizes.small,
      fontWeight: '600',
      color: COLORS.text.secondary,
      textAlign: align,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    }}
  >
    {children}
  </th>
);

export const TableBody: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <tbody>{children}</tbody>
);

export const TableRow: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
}> = ({ children, onClick }) => (
  <tr
    onClick={onClick}
    style={{
      borderBottom: `1px solid ${COLORS.border.default}`,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'background-color 0.1s ease',
    }}
    onMouseEnter={(e) => onClick && (e.currentTarget.style.backgroundColor = COLORS.bg.hover)}
    onMouseLeave={(e) => onClick && (e.currentTarget.style.backgroundColor = 'transparent')}
  >
    {children}
  </tr>
);

export const TableCell: React.FC<{
  children?: React.ReactNode;
  align?: 'left' | 'center' | 'right';
}> = ({ children, align = 'left' }) => (
  <td
    style={{
      padding: `${SPACING[8]} ${SPACING[8]}`,
      fontFamily: TYPOGRAPHY.body.fontFamily,
      fontSize: TYPOGRAPHY.body.sizes.default,
      color: COLORS.text.primary,
      textAlign: align,
    }}
  >
    {children}
  </td>
);

// ============================================================================
// OVERLAY COMPONENTS
// ============================================================================

// Modal (centered dialog)
export const Modal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ isOpen, onClose, title, children, footer }) => {
  // Lock body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: Z_INDEX.modal,
        padding: SPACING[8],
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: COLORS.bg.panel,
          border: `1px solid ${COLORS.border.default}`,
          borderRadius: RADIUS,
          maxWidth: '600px',
          width: '100%',
          maxHeight: 'calc(100vh - 16px)',
          display: 'flex',
          flexDirection: 'column',
          margin: 'auto',
          position: 'relative',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: SPACING[12],
            borderBottom: `1px solid ${COLORS.border.default}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <h3
            style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h3,
              fontWeight: TYPOGRAPHY.headings.weight,
              color: COLORS.text.primary,
              margin: 0,
            }}
          >
            {title}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: COLORS.text.secondary,
              cursor: 'pointer',
              padding: SPACING[4],
              fontSize: '20px',
              lineHeight: 1,
              minWidth: '24px',
              minHeight: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: SPACING[12],
            overflowY: 'auto',
            flex: 1,
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div
            style={{
              padding: SPACING[12],
              borderTop: `1px solid ${COLORS.border.default}`,
              display: 'flex',
              gap: SPACING[8],
              justifyContent: 'flex-end',
              flexShrink: 0,
              flexWrap: 'wrap',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

// Tab Navigation
export const Tabs: React.FC<{
  tabs: { id: string; label: string }[];
  activeTab: string;
  onChange: (tabId: string) => void;
}> = ({ tabs, activeTab, onChange }) => (
  <div
    style={{
      display: 'flex',
      gap: SPACING[4],
      borderBottom: `1px solid ${COLORS.border.default}`,
      marginBottom: SPACING[16],
    }}
  >
    {tabs.map((tab) => {
      const isActive = tab.id === activeTab;
      return (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          style={{
            padding: `${SPACING[8]} ${SPACING[12]}`,
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.default,
            fontWeight: isActive ? '600' : '500',
            color: isActive ? COLORS.accent.default : COLORS.text.primary,
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: isActive ? `2px solid ${COLORS.accent.default}` : '2px solid transparent',
            cursor: 'pointer',
            transition: 'all 0.1s ease',
            marginBottom: '-1px',
          }}
          onMouseEnter={(e) => !isActive && (e.currentTarget.style.color = COLORS.text.primary)}
          onMouseLeave={(e) => !isActive && (e.currentTarget.style.color = COLORS.text.secondary)}
        >
          {tab.label}
        </button>
      );
    })}
  </div>
);
